/* eslint-disable camelcase */

import React, { createContext, useContext, useEffect, useReducer, useState } from 'react'

import { useCart } from '../../hooks/useCart';
import { Product, ProductVariant } from '../../util/typings';
import { STEP_ORDER_GIFT, STEP_ORDER_ME, SUBSCRIPTION_TYPES } from './constants';
import { ISubAtcItem, ISubBuilderContext, ISubBuilderPurchaseType, ISubBuilderSettings, ISubBuilderState, ISubBuilderStep } from './types';
import { findKgVariantOnProduct, getSettings, isExcludedDate, isKgVariant } from './utils';


const hook = (): ISubBuilderContext => {

  const { addToCart } = useCart()

  const [settings, setSettings] = useState(null as ISubBuilderSettings)
  
  const reducer = (state: ISubBuilderState, action: {type: string, payload: Record<string, any>}) : ISubBuilderState => {
    switch (action.type) {
      case 'update':
        return { ...state, ...action.payload }
      case 'replace':
        return action.payload as ISubBuilderState
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    step: null,
    subscriptionType: null,
    productHandle: null,
    productHandles: null,
    isBothProducts: false,
    giftProductHandle: null,
    quantityOption: null,
    grindOption: null,
    giftGrindOption: null,
    durationOption: null,
    durationGiftOption: null,
    durationGiftVariant: null,
    deliveryDate: null,
    intervalFrequency: null
  } as ISubBuilderState)


  const {
    step,
    subscriptionType,
    productHandle,
    productHandles,
    grindOption,
    giftGrindOption,
    durationOption,
    durationGiftVariant
  } = state;

  const getCurrentStepList = () : ISubBuilderStep[] => {
    if (!subscriptionType) return []
    if (subscriptionType === 'gift') {
      return STEP_ORDER_GIFT
    }
    if (subscriptionType === 'me') {
      return STEP_ORDER_ME
    }
    return []
  }

  const getCurrentProduct = (): Product|null => {
    if (!settings) {
      return null
    }
    return settings.products[subscriptionType].find(({ product }) => product.handle === productHandle).product
  }

  const setSubscriptionType = (newType: ISubBuilderPurchaseType | null) => {
    if (!subscriptionType) {
      dispatch({ type: 'update', payload: { subscriptionType: null } })
    }
    if (SUBSCRIPTION_TYPES.includes(newType)) {
      dispatch({ type: 'update', payload: { subscriptionType: newType } })
    } else {
      console.info(`No such subscription type ${newType}`)
    }
  }

  const getGiftProducts = (): Product[] => {
    return settings.products.gift.filter(product => productHandles.includes(product.product.handle)).map(({product}) => product)
  }
  
  const getPossibleVariants = (): ProductVariant[] => {
    const product = getCurrentProduct();
    if (!product) {
      return []
    }
    const possibleVariants = product.variants.filter(variant => {
      if (isKgVariant(variant)) return false
      return (grindOption === variant.option1 || !variant.option1 || !grindOption) && (durationOption === parseInt(variant.option2) || !variant.option2 || !durationOption)
    })

    if (possibleVariants.length > 0) {
      return possibleVariants
    }
    console.info('Could not find variant')
    return []
  }



  const getCurrentVariant = (): ProductVariant | null => {

    const possibleVariants = getPossibleVariants()
    if (possibleVariants && possibleVariants.length === 1) {
      return possibleVariants[0]
    }
    return null
  }

  const getCurrentGiftVariant = (): ProductVariant | null => {
    if (!durationGiftVariant) {
      return null
    }
    return durationGiftVariant
  }

  const getKgVariant = (): ProductVariant | null => {
    if (!state.quantityOption || !settings || state.quantityOption % 4 !== 0) return null
    const currentHandle = subscriptionType === 'me' ? productHandle : state.giftProductHandle
    const currentProductEntry = settings.products[subscriptionType]?.find(
      ({ product }) => product.handle === currentHandle
    )
    if (!currentProductEntry) return null
    const currentGrind = subscriptionType === 'me' ? grindOption : state.giftGrindOption
    return findKgVariantOnProduct(currentProductEntry.product, currentGrind)
  }


  const setProductHandle = (newHandle: null | string) => {
    if (!newHandle) {
      dispatch({ type: 'update', payload: { productHandle: null } })
      return
    }
    const allHandles = [...settings.products.gift.map(({ product }) => product.handle), ...settings.products.me.map(({ product }) => product.handle)]
    if (allHandles.includes(newHandle)) {
      dispatch({ type: 'update', payload: { productHandle: newHandle } })
    } else {
      console.info('No such handle')
    }
  }

  const setProductHandles = (newHandles: null | string[]) => {
    if (!newHandles) {
      dispatch({ type: 'update', payload: { productHandles: null } })
      return
    }
    const allHandles = [...settings.products.gift.map(({ product }) => product.handle)]
    if (allHandles.length) {
      dispatch({ type: 'update', payload: { productHandles: newHandles } })
    }
  }

  const setIsBothProducts = (isBothProducts: boolean) => {
    dispatch({ type: 'update', payload: { isBothProducts: isBothProducts } })
  }

  const setStep = (newStep: ISubBuilderStep) => {
    if (!newStep) {
      window.history.replaceState(state, '', window.location.href)
      dispatch({ type: 'update', payload: { step: null } })
      window.history.pushState(state, '', window.location.href)
    }
    const steps = getCurrentStepList()
    if (steps.includes(newStep)) {
      window.history.replaceState(state, '', window.location.href)
      dispatch({ type: 'update', payload: { step: newStep } })
      window.history.pushState(state, '', window.location.href)
    } else {
      console.info(`${newStep} doesn't exist in the steps.`)
    }
  }

  const moveStep = (by: -1 | 1) => {
    const steps = getCurrentStepList();
    const currentIndex = steps.indexOf(step)
    const newIndex = Math.min(Math.max(-1, currentIndex + by), steps.length - 1);
    if (currentIndex > -1) {
      const newStep = steps[newIndex]
      window.history.replaceState(state, '', window.location.href)
      dispatch({ type: 'update', payload: { step: newStep } })
      window.history.pushState(state, '', window.location.href)
    } else if (currentIndex === -1) {
      window.history.replaceState(state, '', window.location.href)
      dispatch({ type: 'update', payload: { step: null } })
      window.history.pushState(state, '', window.location.href)
    } else {
      console.info('No step')
    }
  }

  const setGrindOption = (newOptionName: string | null, bool: boolean) => {
    if (!newOptionName) {
      dispatch({ type: 'update', payload: { grindOption: null } })
      return
    }
    const allGrindOptions = settings.steps.grind.options.map(({ optionName }) => optionName)
    if (!allGrindOptions.includes(newOptionName)) {
      console.info(`Option ${newOptionName} does not exist in settings`)
      return
    }
    if(bool){
      const currentProductOptions = getCurrentProduct().variants.map(({ option1 }) => option1)
      if (!currentProductOptions.includes(newOptionName)) {
        console.info(`Option ${newOptionName} does not exist in product options (${currentProductOptions.join(', ')})`)
        return
      }
      
      dispatch({ type: 'update', payload: { grindOption: newOptionName } })
    } else {
      dispatch({ type: 'update', payload: { giftGrindOption: newOptionName } })
    }
  }

  const setQuantityOption = (newQuantity: number|null) => {
    if (!newQuantity) {
      dispatch({ type: 'update', payload: { quantityOption: null } })
      return
    }
    const allQuantityOptions = settings.steps.quantity.options.map(({ quantity }) => quantity)
    if (!allQuantityOptions.includes(newQuantity)) {
      console.info(`Quaitity ${newQuantity} does not exist in settings`)
      return
    }
    dispatch({ type: 'update', payload: { quantityOption: newQuantity } })
  }

  const setDurationOption = (newDuration: number|null) => {
    if (!newDuration) {
      dispatch({ type: 'update', payload: { durationOption: null } })
      return
    }
    const allDurationOptions = settings.steps.duration.options.map(({ optionNumber }) => optionNumber)
    if (!allDurationOptions.includes(newDuration)) {
      console.info(`Duration ${newDuration} does not exist in settings`)
      return
    }
    const currentProductOptions = getCurrentProduct().variants.map(({ option2 }) => parseInt(option2))
    if (!currentProductOptions.includes(newDuration)) {
      console.info(`Option ${newDuration} does not exist in product options (${currentProductOptions.join(', ')})`)
      return
    }
    dispatch({ type: 'update', payload: { durationOption: newDuration } })
  }
  //This sets the variant which in itself is a duration
  const setDurationForGift = (handle: string) => {
    const chosenProduct = settings.products.gift.find(({ product }) => product.handle === handle).product
    
    if (!chosenProduct) {
      return null
    }
    const variant = chosenProduct.variants.find(vnt => {
      if (isKgVariant(vnt)) return false
      if (!vnt.option2) {
        console.info('PLEASE check that the gift products have the grind options set as the second option on the product.')
      }
      return vnt.option2.includes(giftGrindOption)
    })
    
    if (!variant) {
      dispatch({type: 'update', payload: {durationGiftVariant: null}})
    }



    dispatch({type: 'update', payload: {durationGiftVariant: variant}})
    dispatch({ type: 'update', payload: { giftProductHandle: chosenProduct.handle } })
    //Got to make sure that the titles have the number as the second word
    dispatch({ type: 'update', payload: { durationGiftOption: parseInt(chosenProduct.title.split(' ')[1]) }})
  }

  const setDeliveryDate = (deliveryDate: null|Date) : void => {
    if (!deliveryDate) {
      dispatch({ type: 'update', payload: { deliveryDate: null } })
      return
    }
    const excludedDates = settings.steps.date.excludedDates
    if (isExcludedDate(deliveryDate, excludedDates) || deliveryDate.getDay() === 0) {
      console.info(`${deliveryDate.toString()} is not allowed.`)
      return
    }
    dispatch({ type: 'update', payload: { deliveryDate: deliveryDate } })

  }


  const setIntervalFrequency = (freq: number | null) : void => {
    dispatch({ type: 'update', payload: { intervalFrequency: freq } })
  }

  const addSubItemToCart = (item: ISubAtcItem): void => {
    addToCart([item], false, () => {
      //window.location.href = '/cart'
    })
  }

  const loadSettings = (): void => {
    console.info('loading settings...')
    const newSettings = getSettings()
    setSettings(newSettings)
  }
  
  useEffect(() => {
    //@TODO, quick fix for now, probably need a better way of doing this...
    const subPortal = document.querySelector('[data-app-layer-portal="sub-builder"]')
    if (!subPortal) {
      /* eslint-disable react/display-name */
      return () => null
    }
    //populate the settings
    loadSettings()
    window.history.pushState(state, '', window.location.href)
    //set his history listener
    const popStateCallBack = (event: PopStateEvent) => {
      if (event.state) {
        dispatch({ type: 'replace', payload: event.state })
      }
    }
    window.addEventListener('popstate', popStateCallBack)
    
    document.addEventListener('shopify:section:load', loadSettings)

    return () => {
      window.removeEventListener('popstate', popStateCallBack)
      document.removeEventListener('shopify:section:load', loadSettings)
    }
  }, [])


  //when step changes, reset the current steps options
  useEffect(() => {
    if(window['dataLayer']) {
      window['dataLayer'].push({'event': 'step change'})
      switch (step) {
        case 'quantity':
          dispatch({ type: 'update', payload: { quantityOption: null } })
          break;
        case 'duration':
          dispatch({ type: 'update', payload: { durationOption: null } })
          dispatch({ type: 'update', payload: { durationGiftOption: null } })
          break;
        case 'grind':
          dispatch({ type: 'update', payload: { grindOption: null } })
          break;
        case 'roast':
          dispatch({ type: 'update', payload: { productHandle: null } })
          dispatch({ type: 'update', payload: { productHandles: null } })
          dispatch({ type: 'update', payload: { giftProductHandle: null } })
          break;
        case 'date':
          dispatch({ type: 'update', payload: { deliveryDate: null } })
          break;
        case 'routine':
          dispatch({ type: 'update', payload: { quantityOption: null } })
          dispatch({ type: 'update', payload: { intervalFrequency: null } })
          break;
        case 'summary':
          break;
        default:
          dispatch({ type: 'update', payload: { subscriptionType: null } })
          break;
      }
    }
  }, [step])

  //once sub type is chosen, start the steps:
  useEffect(() => {
    const stepList = getCurrentStepList();
    dispatch({ type: 'update', payload: { step: stepList[0] } })
  }, [subscriptionType])

  //if change product, unset the options
  useEffect(() => {
    dispatch({ type: 'update', payload: { grindOption: null } })
    dispatch({ type: 'update', payload: { giftGrindOption: null } })
    dispatch({ type: 'update', payload: { quantityOption: null } })
    dispatch({ type: 'update', payload: { durationOption: null } })
    dispatch({ type: 'update', payload: { durationGiftOption: null } })
    dispatch({ type: 'update', payload: { deliveryDate: null } })
  }, [productHandle, productHandles])

  return {
    settings,
    state,
    actions: {
      setStep,
      addSubItemToCart,
      moveStep,
      setSubscriptionType,
      setProductHandle,
      setProductHandles,
      setIsBothProducts,
      setGrindOption,
      setQuantityOption,
      setDurationOption,
      setDurationForGift,
      setDeliveryDate,
      setIntervalFrequency
    },
    getters: {
      getCurrentStepList,
      getCurrentVariant,
      getPossibleVariants,
      getGiftProducts,
      getCurrentGiftVariant,
      getKgVariant
    }
  }
};

const SubBuilderContext = createContext(null);

export const SubBuilderProvider: React.FunctionComponent = ({ children }) => {
  const value = hook()
  return (<SubBuilderContext.Provider value={value}>{children}</ SubBuilderContext.Provider>)
}

export const useSubBuilder = (): ISubBuilderContext  => useContext(SubBuilderContext)

